import React from 'react';

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const RentToOwnStockExecSummary = ({ investorResult, borrowerResult, params }) => {
  if (!investorResult || !borrowerResult || !params) {
    return <div className="text-center text-gray-500">No data available.</div>;
  }

  return (
    <div className="bg-blue-50 p-6 rounded-lg text-sm text-gray-800 leading-relaxed space-y-4">
      <h3 className="text-lg font-bold text-blue-900">Executive Summary</h3>

      <p>
        This <strong>Rent-to-Own + Stock Reinvestment</strong> model simulates a hybrid investment scenario over a {investorResult.termYears}-year period,
        where the investor purchases a property worth <strong>{formatCurrency(params.propertyValue)}</strong> and offers a rent-to-own path to an entrepreneur.
        The investor receives full rental income, a portion of which goes toward equity buyout, while a defined percentage (
        <strong>{params.reinvestmentSplit}%</strong>) of the investor’s revenue (rent and buyout) is reinvested quarterly into a stock portfolio that compounds over time.
      </p>

      <h4 className="font-semibold text-blue-800">📈 Investor Highlights</h4>
      <ul className="list-disc list-inside space-y-1">
        <li>Total Rent Collected: <strong>{formatCurrency(investorResult.totalRentCollected)}</strong></li>
        <li>Net Cashflow (after expenses): <strong>{formatCurrency(investorResult.netCashflow)}</strong></li>
        <li>Reinvested Capital (Stock Market): <strong>{formatCurrency(investorResult.reinvestedCapital)}</strong></li>
        <li>Final Property Value: <strong>{formatCurrency(investorResult.finalValue)}</strong></li>
        <li>Total Exit Value: <strong>{formatCurrency(investorResult.exitValue)}</strong> (includes rent, equity, reinvestments minus sell cost)</li>
        <li>Total ROI: <strong>{investorResult.totalROI}%</strong></li>
        <li>Annualized ROI: <strong>{investorResult.annualizedROI}%</strong></li>
        <li>Cash-on-Cash Return: <strong>{investorResult.cashOnCashReturn}%</strong></li>
      </ul>

      <h4 className="font-semibold text-purple-800">🏠 Entrepreneur Highlights</h4>
      <ul className="list-disc list-inside space-y-1">
        <li>Average Monthly Rent: <strong>{formatCurrency(borrowerResult.monthlyRentPayment)}</strong></li>
        <li>Total Rent Paid Over Time: <strong>{formatCurrency(borrowerResult.totalRentPaid)}</strong></li>
        <li>Equity Bought Out: <strong>{formatCurrency(borrowerResult.totalBuyout)}</strong></li>
        <li>Total Expenses (Tax, Insurance, Maintenance): <strong>{formatCurrency(borrowerResult.totalExpenses)}</strong></li>
        <li>Total Capital Invested: <strong>{formatCurrency(borrowerResult.totalPayment)}</strong></li>
        <li>Final Equity (Ownership Share): <strong>{formatCurrency(borrowerResult.equityBuilt)}</strong></li>
        <li>Exit Value (based on ownership): <strong>{formatCurrency(borrowerResult.exitValue)}</strong></li>
      </ul>

      <p>
        This model offers a unique structure where the <strong>investor benefits from immediate income and future stock market gains</strong>,
        while the <strong>entrepreneur builds real equity</strong> through consistent rent contributions. It is ideal for scenarios that require
        low-risk capital rotation, long-term growth alignment, and equitable outcomes for both parties — without traditional interest-bearing debt.
      </p>
    </div>
  );
};

export default RentToOwnStockExecSummary;
